// src/components/OnboardingChatbot.js

import React, { useEffect, useMemo } from 'react';
import Chatbot from 'react-simple-chatbot';
import { ThemeProvider } from 'styled-components';
import botIcon from '../assets/MercaditiAI_Paco_Avatar_3D_40x40.png';
import AIResponse from './AIResponse';
import AIBusinessTypeChecker from './AIBusinessTypeChecker';
import SolutionCheckboxList from './SolutionCheckboxList';
import SelectedOptionsSummary from './SelectedOptionsSummary';
import FormatNameStep from './FormatNameStep';
import ClientSetupForm from './ClientSetupForm';
import { useTranslation } from 'react-i18next';

const OnboardingChatbot = () => {
  const { t, i18n } = useTranslation();

  // Chat steps
  // This array defines the steps of the chatbot conversation.
  const steps = useMemo(() => [
    {
      id: 'welcome',
      message: t('pacobot.welcome'),
      trigger: 'name',
    },
    {
      id: 'name',
      user: true,
      trigger: 'formatName',
    },
    {
      id: 'formatName',
      component: <FormatNameStep />,
      waitAction: true,
      asMessage: false,
      trigger: 'saluteName'
    },
    {
      id: 'saluteName',
      message: ({ previousValue }) =>
        t('pacobot.salute', { previousValue }),
      trigger: 'businessDesc'
    },
    {
      id: 'businessDesc',
      user: true,
      trigger: 'gptTypeChecker',
    },
    {
      id: 'gptTypeChecker',
      component: <AIBusinessTypeChecker />,
      waitAction: true,
      asMessage: true,
      trigger: 'confirmSuggestedType',
    },
    {
      id: 'confirmSuggestedType',
      message: ({ steps }) =>
        t('pacobot.confirmType', { value: steps.gptTypeChecker.value }),
      trigger: 'confirmTypeChoice',
    },
    {
      id: 'confirmTypeChoice',
      options: [
        { value: 'yes', label: t('pacobot.confirm.yes'), trigger: 'saveType' },
        { value: 'no', label: t('pacobot.confirm.no'), trigger: 'retryBusinessType' },
      ],
    },
    {
      id: 'saveType',
      message: t('pacobot.perfectContinue'),
      metadata: {
        saveAs: 'businessType',
        from: 'gptTypeChecker',
      },
      trigger: 'goal',
    },
    {
      id: 'retryBusinessType',
      message: t('pacobot.retryType'),
      trigger: 'manualTypeEntry',
    },
    {
      id: 'manualTypeEntry',
      user: true,
      trigger: 'saveManualType',
    },
    {
      id: 'saveManualType',
      message: t('pacobot.thanksContinue'),
      metadata: {
        saveAs: 'businessType',
        from: 'manualTypeEntry',
      },
      trigger: 'gptTypeChecker'
    },
    {
      id: 'goal',
      message: t('pacobot.howCanHelp'),
      trigger: 'goalInput'
    },
    {
      id: 'goalInput',
      user: true,
      trigger: 'sendDataToAI'
    },
    {
      id: 'sendDataToAI',
      message: t('pacobot.processing'),
      trigger: 'aiResponse',
    },
    {
      id: 'aiResponse',
      component: (<AIResponse />),
      waitAction: true,
      asMessage: true,
      trigger: 'solutionOptions'
    },
    {
      id: 'solutionOptions',
      message: t('pacobot.suggestions'),
      trigger: 'solutionChoices'
    },
    {
      id: 'solutionChoices',
      component: <SolutionCheckboxList />,
      waitAction: true,
      asMessage: true,
      trigger: 'summaryStep'
    },
    {
      id: 'summaryStep',
      component: <SelectedOptionsSummary />,
      waitAction: true,
      asMessage: true,
      trigger: 'engagementChoice'
    },
    {
      id: 'engagementChoice',
      message: t('pacobot.planReady'),
      trigger: 'engagementOptions'
    },
    {
      id: 'engagementOptions',
      waitAction: true,
      options: [
        {
          value: t('pacobot.engage.autoSetup'),
          label: t('pacobot.engage.autoSetup'),
          trigger: 'clientInfoFormIntro',
        },
        {
          value: t('pacobot.engage.assist'),
          label: t('pacobot.engage.assist'),
          trigger: 'clientInfoFormIntro',
        },
        {
          value: t('pacobot.engage.infoOnly'),
          label: t('pacobot.engage.infoOnly'),
          trigger: 'clientInfoFormIntro',
        }
      ]
    },
    {
      id: 'clientInfoFormIntro',
      message: t('pacobot.fillInfo'),
      trigger: 'clientInfoFormComponent',
    },
    {
      id: 'clientInfoFormComponent',
      component: <ClientSetupForm />,
      waitAction: true,
      asMessage: false,
      trigger: 'formSavedConfirmation'
    },
    {
      id: 'formSavedConfirmation',
      waitAction: true,
      message: t('pacobot.infoSaved'),
      trigger: 'registration',
    },
    {
      id: 'registration',
      message: t('pacobot.end.thanks'),
      end: true,
    }
  ], [i18n.language, t]);

  // Optional: customize theme
  const theme = {
    background: '#ffffff',
    fontFamily: 'Bricolage Grotesque',
    headerBgColor: '#7C3AED', // purple header
    headerFontColor: '#fff',
    headerFontSize: '24px',
    botBubbleColor: '#7C3AED', // purple bubble
    botFontColor: '#fff',
    userBubbleColor: '#D8B4FE', // light purple bubble
    userFontColor: '#333',
  };

  return (
    <ThemeProvider theme={theme}>
      <Chatbot
        steps={steps}
        botAvatar={botIcon}
        headerTitle={t('pacobot.title')}
        placeholder={t('pacobot.placeholder')}
      />
    </ThemeProvider>
  );
};

export default OnboardingChatbot;
